import { useRemoveUserBreak } from '@tovala/browser-apis-combinedapi'
import { useToast } from 'contexts/toast'
import { ReactNode, useState } from 'react'
import ConfirmationDialog, {
  ConfirmationHeader,
  ConfirmationBody,
  ConfirmationButtons,
} from 'components/common/ConfirmationDialog'
import { Button, ButtonLoading, ErrorDisplay } from '@tovala/component-library'

const ResumeDeliveries = ({
  children,
  userID,
}: {
  children: (props: { onClickResume: () => void }) => ReactNode
  userID: number
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const { openToast } = useToast()

  const {
    mutate: removeUserBreak,
    isPending: isRemovingUserBreak,
    error: removeUserBreakError,
  } = useRemoveUserBreak({
    onSuccess: () => {
      openToast({
        heading: 'Meal Deliveries Resumed',
        message: 'Please double check any pending orders.',
        type: 'success',
      })
    },
    onError: () => {
      openToast({
        goodErrorMessaging: {
          helpToFix: 'Please try again.',
          whatHappened: 'Unable to resume meal deliveries',
          why: 'There was an error resuming your meal deliveries.',
        },
        type: 'error',
      })
    },
  })

  return (
    <>
      {showConfirmationDialog && (
        <ResumeDeliveriesConfirmation
          isRemovingUserBreak={isRemovingUserBreak}
          onClickClose={() => {
            setShowConfirmationDialog(false)
          }}
          onClickConfirm={() => {
            removeUserBreak(
              { userID },
              {
                onSuccess: () => {
                  setShowConfirmationDialog(false)
                },
              },
            )
          }}
          removeUserBreakError={removeUserBreakError}
        />
      )}
      {children({
        onClickResume: () => {
          setShowConfirmationDialog(true)
        },
      })}
    </>
  )
}

export default ResumeDeliveries

export const ResumeDeliveriesConfirmation = ({
  removeUserBreakError,
  isRemovingUserBreak,
  onClickClose,
  onClickConfirm,
}: {
  removeUserBreakError: Error | null
  isRemovingUserBreak: boolean
  onClickClose(): void
  onClickConfirm(event: React.MouseEvent<HTMLButtonElement>): void
}) => {
  return (
    <ConfirmationDialog onRequestClose={onClickClose}>
      <ConfirmationHeader heading="Resume Deliveries?" />
      <ConfirmationBody>
        <div className="py-16 md:py-12">
          <p className="mx-auto max-w-sm text-body-lg">
            Your scheduled break will end and deliveries will resume.
          </p>
        </div>
      </ConfirmationBody>
      <ConfirmationButtons>
        <div className="max-w-sm space-y-4">
          {removeUserBreakError && (
            <ErrorDisplay
              helpToFix="Please wait a few minutes and try again."
              why="We couldn't resume your meal deliveries due to a technical issue on our end."
            />
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button buttonStyle="stroke" onClick={onClickClose} size="large">
              Cancel
            </Button>

            <ButtonLoading
              isLoading={isRemovingUserBreak}
              onClick={onClickConfirm}
              size="large"
            >
              Resume Deliveries
            </ButtonLoading>
          </div>
        </div>
      </ConfirmationButtons>
    </ConfirmationDialog>
  )
}
